import * as React from "react"
import { useNavigate } from "react-router-dom"
import { CalendarDays, CircleDollarSign, Search, UserRound, Wallet } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { useGroupTransactions } from "@/hooks/repository"
import { useActiveGroup } from "@/stores/group.store"
import { cn, formatCurrency } from "@/lib/utils"
import type { TransactionRecord } from "@/services/storage/storage.service"

const normalizeSearchText = (value: unknown) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()

const compactAmount = (amount: number | undefined) =>
  typeof amount === "number" && Number.isFinite(amount)
    ? [String(amount), amount.toFixed(2), amount.toLocaleString("en-US")]
    : []

const fuzzyIncludes = (haystack: string, needle: string) => {
  if (!needle) return true
  if (haystack.includes(needle)) return true

  let haystackIndex = 0
  for (const char of needle) {
    haystackIndex = haystack.indexOf(char, haystackIndex)
    if (haystackIndex === -1) return false
    haystackIndex += 1
  }
  return true
}

const getTransactionLabel = (transaction: TransactionRecord) => {
  if (transaction.type === "settlement") return "Settlement"
  if (transaction.type === "pool_event") {
    return transaction.event_type === "insert" ? "Pool insert" : "Pool expense"
  }
  return transaction.category?.name || "Expense"
}

const getTransactionPeople = (transaction: TransactionRecord) => {
  if (transaction.type === "settlement") {
    return [transaction.from_user?.name, transaction.to_user?.name].filter(Boolean).join(" → ")
  }
  return transaction.user?.name || "Unknown payer"
}

const getTransactionDate = (transaction: TransactionRecord) => {
  const rawDate = transaction.date || transaction.created_at
  if (!rawDate) return "No date"

  const date = new Date(rawDate)
  if (Number.isNaN(date.getTime())) return "No date"

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date)
}

const getSearchText = (transaction: TransactionRecord) => {
  const amountParts = compactAmount(transaction.amount)

  return normalizeSearchText([
    transaction.type,
    transaction.event_type,
    transaction.description,
    transaction.currency_code,
    ...amountParts,
    transaction.amount && transaction.currency_code
      ? formatCurrency(transaction.amount, transaction.currency_code)
      : undefined,
    transaction.user?.name,
    transaction.from_user?.name,
    transaction.to_user?.name,
    transaction.category?.name,
    transaction.category?.emoji,
    transaction.pool_name,
    transaction.date,
    transaction.created_at,
    getTransactionDate(transaction),
  ].filter(Boolean).join(" "))
}

const scoreResult = (transaction: TransactionRecord, query: string) => {
  const normalizedQuery = normalizeSearchText(query)
  const description = normalizeSearchText(transaction.description)
  const people = normalizeSearchText(getTransactionPeople(transaction))
  const category = normalizeSearchText(transaction.category?.name)
  const amount = normalizeSearchText(compactAmount(transaction.amount).join(" "))
  const fullText = getSearchText(transaction)

  if (!fuzzyIncludes(fullText, normalizedQuery)) return null

  let score = 0
  if (description.startsWith(normalizedQuery)) score += 80
  if (people.startsWith(normalizedQuery)) score += 70
  if (category.startsWith(normalizedQuery)) score += 60
  if (amount.startsWith(normalizedQuery)) score += 55
  if (description.includes(normalizedQuery)) score += 35
  if (people.includes(normalizedQuery)) score += 30
  if (category.includes(normalizedQuery)) score += 25
  if (amount.includes(normalizedQuery)) score += 25
  if (fullText.includes(normalizedQuery)) score += 10

  return score
}

interface GlobalSearchProps {
  /** Focus the input on mount (used by the mobile search overlay). */
  autoFocus?: boolean
  /** Called after a result is chosen and navigation happens. */
  onSelect?: () => void
}

export function GlobalSearch({ autoFocus = false, onSelect }: GlobalSearchProps = {}) {
  const navigate = useNavigate()
  const { activeGroupId } = useActiveGroup()
  const { data: transactions, isLoading } = useGroupTransactions(activeGroupId || "", {
    enabled: !!activeGroupId,
  })
  const transactionRecords = transactions ?? []
  const [query, setQuery] = React.useState("")
  const [isOpen, setIsOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState(0)
  const deferredQuery = React.useDeferredValue(query)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  const results = React.useMemo(() => {
    const normalizedQuery = normalizeSearchText(deferredQuery)
    if (!normalizedQuery || normalizedQuery.length < 2) return []

    return transactionRecords
      .map((transaction) => ({
        transaction,
        score: scoreResult(transaction, normalizedQuery),
      }))
      .filter((result): result is { transaction: TransactionRecord; score: number } =>
        result.score !== null
      )
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score
        return new Date(b.transaction.created_at).getTime() - new Date(a.transaction.created_at).getTime()
      })
      .slice(0, 8)
  }, [deferredQuery, transactionRecords])

  React.useEffect(() => {
    setActiveIndex(0)
  }, [deferredQuery])

  React.useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("pointerdown", handlePointerDown)
    return () => document.removeEventListener("pointerdown", handlePointerDown)
  }, [])

  const openTransaction = (transaction: TransactionRecord) => {
    setIsOpen(false)
    navigate(`/expenses?highlight=${encodeURIComponent(transaction.id)}`)
    onSelect?.()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setIsOpen(false)
      return
    }

    if (!isOpen || results.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      setActiveIndex((index) => (index + 1) % results.length)
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      setActiveIndex((index) => (index - 1 + results.length) % results.length)
    }

    if (event.key === "Enter") {
      event.preventDefault()
      openTransaction(results[activeIndex].transaction)
    }
  }

  const hasQuery = normalizeSearchText(query).length >= 2

  return (
    <div ref={containerRef} className="relative">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input
        ref={inputRef}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
          setIsOpen(true)
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Search amount, person, place..."
        className="h-10 rounded-full border-border/70 bg-background/90 pl-10 pr-4 shadow-sm backdrop-blur"
        aria-label="Search transactions"
        aria-expanded={isOpen}
        aria-controls="global-search-results"
      />

      {isOpen && hasQuery && (
        <div
          id="global-search-results"
          className="absolute left-0 right-0 top-12 z-50 overflow-hidden rounded-2xl border bg-popover text-popover-foreground shadow-2xl shadow-black/15"
        >
          <div className="border-b bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            {isLoading ? "Searching local data" : `${results.length} result${results.length === 1 ? "" : "s"}`}
          </div>

          {results.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <p className="text-sm font-medium">No matching transactions</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Try an amount, payer, category, or description.
              </p>
            </div>
          ) : (
            <div className="max-h-[430px] overflow-y-auto p-2">
              {results.map(({ transaction }, index) => {
                const isActive = index === activeIndex
                const amount = transaction.amount ?? 0
                const currency = transaction.currency_code || "EUR"

                return (
                  <button
                    key={transaction.id}
                    type="button"
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => openTransaction(transaction)}
                    className={cn(
                      "flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors",
                      isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/70"
                    )}
                  >
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      {transaction.type === "pool_event" ? (
                        <Wallet className="h-4 w-4" />
                      ) : transaction.type === "settlement" ? (
                        <UserRound className="h-4 w-4" />
                      ) : (
                        <CircleDollarSign className="h-4 w-4" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <p className="truncate text-sm font-semibold">
                          {transaction.description || getTransactionLabel(transaction)}
                        </p>
                        <p className="shrink-0 text-sm font-semibold">
                          {formatCurrency(amount, currency)}
                        </p>
                      </div>

                      <div className="mt-1 flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
                        <span className="truncate">{getTransactionPeople(transaction)}</span>
                        <span aria-hidden="true">•</span>
                        <span className="truncate">{getTransactionLabel(transaction)}</span>
                      </div>

                      <div className="mt-2 flex items-center gap-2">
                        <Badge variant="secondary" className="rounded-full px-2 py-0 text-[10px] font-medium">
                          {transaction.type.replace("_", " ")}
                        </Badge>
                        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                          <CalendarDays className="h-3 w-3" />
                          {getTransactionDate(transaction)}
                        </span>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
